/* eslint-disable no-restricted-globals */
import React from 'react';
import { reverb_search_SearchResponse } from '../rql';
import RQLSearchOverview from './rql_search_overview';
import RQLPagination from './rql_pagination';
import { Filter } from './search_filters';
import { Location } from 'history';
import I18n from 'i18n-js';
import { UpdateURLProvider, useUpdateUrl } from '@reverbdotcom/commons/src/components/with_update_url';
import { defaultUpdateLocation } from '../update_params';

interface IProps {
  error?: boolean;
  loading?: boolean;
  search: reverb_search_SearchResponse;
  location: Location;
  hasPagination?: boolean;
  hasSearchOverview?: boolean;
  showSimilarListingsMessage?: boolean;
  cards: React.ReactNode; // this should be a <Tiles> component of cards
  fallbackCards?: React.ReactNode;
  lowResultsAlert?: React.ReactNode;
  subqueryRows?: React.ReactNode;
  followButton?: React.ReactNode;
  followBanner?: React.ReactNode;
  aboveGridBanner?: React.ReactNode; // Ad Placement Banner
  relatedSearchRow?: React.ReactNode; // MarketplaceCSPRow
  secondaryRelatedSearchRow?: React.ReactNode;
  tipText?: string;
  hideClearFiltersButton?: boolean;
  filters?: Filter[];
  totalText?: string; // Used for creating the grid title
  titleOverride?: string;
  useH1Title?: boolean;
  resultsTotal: number;
  query?: string;
  trackingQuery?: string; // Passed down to facets
  setListViewState?: (val: boolean) => void;
  showListViewToggle?: boolean;
  pinTextQuery?: boolean; // ensure search bar always visible above grid, instead of buried within filters
}

export default function RQLSearchGrid(props: IProps) {
  const {
    search,
    showSimilarListingsMessage,
    aboveGridBanner = undefined,
    hasPagination = false,
    hasSearchOverview = false,
    relatedSearchRow = null,
    error = false,
    trackingQuery = '',
    location = null,
    showListViewToggle = false,
    hideClearFiltersButton = false,
    pinTextQuery = false,
    totalText,
    titleOverride,
    resultsTotal,
    tipText,
    loading,
    followButton,
    setListViewState,
    useH1Title = false,
    query,
    lowResultsAlert,
    cards,
    secondaryRelatedSearchRow,
    followBanner,
    subqueryRows,
    fallbackCards,
  } = props;
  const { updateUrl } = useUpdateUrl();
  const filters = props.filters || search?.filters || [];

  return (
    <UpdateURLProvider updateUrl={updateUrl ?? defaultUpdateLocation}>
      <div className="faceted-grid faceted-grid--wide">
        {aboveGridBanner}
        <div className="faceted-grid__inner">
          <div className="faceted-grid__main">
            {showSimilarListingsMessage &&
              <div className="pb-1">
                <h1 className="size-140">{I18n.t('discovery.similarListingsMessage.title')}</h1>
              </div>
            }
            {relatedSearchRow}
            {hasSearchOverview && !error &&
              <RQLSearchOverview
                filters={filters}
                followButton={followButton}
                totalText={totalText}
                titleOverride={titleOverride}
                resultsTotal={resultsTotal}
                tipText={tipText}
                loading={loading}
                trackingQuery={trackingQuery}
                location={location}
                showListViewToggle={showListViewToggle}
                setListViewState={setListViewState}
                hideClearFiltersButton={hideClearFiltersButton}
                useH1Title={useH1Title}
                query={query}
                pinTextQuery={pinTextQuery}
              />
            }
            {lowResultsAlert}
            {cards}
            {hasPagination && !error && search &&
              <RQLPagination
                limit={search.limit}
                total={search.total}
                offset={search.offset}
              />
            }
            {secondaryRelatedSearchRow}
            {!loading && followBanner}
            {subqueryRows}
            {fallbackCards}
          </div>
        </div>
      </div>
    </UpdateURLProvider>
  );
}
